import { Modal, useModals } from 'almanac-ui/Modal'
import { Text } from 'blaze-ui/Text/Text'
import { useState, useCallback } from 'react'
import { ShopifyIntegration, ShopifyProfile, ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { useUpdateExternalIntegration } from 'hooks/queries/externalIntegrations/useUpdateExternalIntegration'
import { useUpdateBrandPlan } from 'hooks/queries/useUpdateBrandPlan'
import { useCurrentBrandPlan } from 'hooks/queries/brandPlans/useCurrentBrandPlan'
import { ShopifyBlogSelect } from 'components/BlogMetadata/components/integrations/ShopifyBlogSelect/ShopifyBlogSelect'
import { ShopifyAuthorSelect } from 'components/BlogMetadata/components/integrations/ShopifyAuthorSelect/ShopifyAuthorSelect'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import Styles from '../IntegrationSuccessModal/IntegrationSuccessModal.module.scss'
import { Heading } from 'almanac-ui/Heading/Heading'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { ShopifyBlog, ShopifyAuthor } from 'api/clients/BlogMetadataRecordsClient'

interface Props {
  integration: ShopifyIntegration
  onClose: () => void
  onPressOutside?: () => void
}

export const ShopifyConnectedSuccessModal = ({ integration, onClose, onPressOutside }: Props) => {
  const { closeModal } = useModals()
  const { mutateAsync: updateExternalIntegration, isLoading } = useUpdateExternalIntegration()
  const { data: brandPlan } = useCurrentBrandPlan()
  const { mutateAsync: updateBrandPlan } = useUpdateBrandPlan({ id: brandPlan?.id || 0 })
  const workspace = useCurrentWorkspace()

  const profile = integration.additional_data as ShopifyProfile
  const availableBlogs: ShopifyBlog[] = profile?.shopify_blogs || []
  const availableAuthors: ShopifyAuthor[] = profile?.shopify_authors || []

  const [selectedBlogId, setSelectedBlogId] = useState<string | undefined>(() => {
    if (profile?.selected_blog_id) {
      return profile.selected_blog_id
    }
    if (availableBlogs.length >= 1) {
      return availableBlogs[0].id
    }
    return undefined
  })

  const [selectedAuthorId, setSelectedAuthorId] = useState<string | undefined>(() => {
    if (profile?.selected_author_id) {
      return profile.selected_author_id
    }
    if (availableAuthors.length === 1) {
      return availableAuthors[0].id
    }
    return undefined
  })

  const handleClose = useCallback(() => {
    closeModal()
    onClose()
  }, [closeModal, onClose])

  const handleSaveDefaults = useCallback(async () => {
    if (!selectedBlogId) {
      showToastMessage('Please select a default blog', 'error')
      return
    }

    if (!selectedAuthorId) {
      showToastMessage('Please select a default author', 'error')
      return
    }

    try {
      // Update the external integration with selected blog and author
      await updateExternalIntegration({
        id: integration.id,
        additional_data: {
          ...profile,
          selected_blog_id: selectedBlogId,
          selected_author_id: selectedAuthorId,
        },
      })

      // Update the brand plan to set Shopify as the selected blog platform
      if (brandPlan && updateBrandPlan) {
        await updateBrandPlan({
          selected_blog_platform: ExternalIntegrationEnum.Shopify,
        })
      }

      showToastMessage('Saved Successfully', 'success')
      handleClose()
    } catch (error) {
      showToastMessage('Failed to save defaults. Please try again.', 'error')
    }
  }, [
    selectedBlogId,
    selectedAuthorId,
    updateExternalIntegration,
    integration.id,
    profile,
    handleClose,
    brandPlan,
    updateBrandPlan,
  ])

  const canSave = selectedBlogId && selectedAuthorId
  const isAutopilot = workspace?.is_virtual_marketer

  return (
    <Modal.Root
      size="sm"
      onPressOutside={onPressOutside}
      onClose={handleClose}
      aria-label="Shopify Account Connected Successfully"
    >
      <Modal.Header
        variant="hero"
        heroImage="https://res.cloudinary.com/almanac/image/upload/v1755871734/blaze_assets/65cfcb6ed487608453ac773ecee3a53be5e327f9_exitid.png"
        heroImageAlt=""
        title={isAutopilot ? `Your Shopify content will start autoposting!` : `Your Shopify account is connected!`}
        headingLevel={1}
      />
      <Modal.Content>
        <div className={Styles.content}>
          <div className={Styles.description}>
            <Text>
              Set your default blog and author. You can update this anytime in Integrations page or when editing a post.
            </Text>
          </div>

          <div className={Styles.fieldsContainer}>
            <div className={Styles.fieldContainer}>
              <Heading level={4}>Default blog for all blog posts</Heading>
              <ShopifyBlogSelect
                blogs={availableBlogs}
                selectedBlogId={selectedBlogId}
                onBlogSelect={setSelectedBlogId}
                placeholder="Select a default blog"
              />
            </div>

            <div className={Styles.fieldContainer}>
              <Heading level={4}>Default author for all blog posts</Heading>
              <ShopifyAuthorSelect
                authors={availableAuthors}
                selectedAuthorId={selectedAuthorId}
                onAuthorSelect={setSelectedAuthorId}
                placeholder="Select a default author"
              />
            </div>
          </div>
        </div>
      </Modal.Content>
      <Modal.Footer>
        <Modal.FooterContent slot="right">
          <Modal.FooterButton variant="primary" onPress={handleSaveDefaults} isDisabled={!canSave || isLoading}>
            {isLoading ? 'Saving...' : 'Confirm'}
          </Modal.FooterButton>
        </Modal.FooterContent>
      </Modal.Footer>
    </Modal.Root>
  )
}
